import { cn } from "@/utils/cn"
import { Slider } from "@/components/ui/slider"
import { Label } from "@/components/ui/label"

interface WeightSliderProps {
  value: number
  onChange: (value: number) => void
  label?: string
  showMarks?: boolean
  disabled?: boolean
  color?: 'default' | 'risk'
  min?: number
  max?: number
  step?: number
  showValue?: boolean
  className?: string
}

export const WeightSlider = ({
  value,
  onChange,
  label,
  showMarks = false,
  disabled = false,
  color = 'default',
  min = 0,
  max = 100,
  step = 1,
  showValue = true,
  className
}: WeightSliderProps) => {
  const getRiskColor = (value: number) => {
    if (value >= 80) return 'text-risk-critical'
    if (value >= 60) return 'text-risk-high'
    if (value >= 30) return 'text-risk-medium'
    return 'text-risk-low'
  }

  const marks = showMarks ? [
    { value: 0, label: '0' },
    { value: 25, label: '25' },
    { value: 50, label: '50' },
    { value: 75, label: '75' },
    { value: 100, label: '100' }
  ] : []

  const handleValueChange = (values: number[]) => {
    onChange(values[0])
  }

  return (
    <div className={cn("space-y-3", className)}>
      {(label || showValue) && (
        <div className="flex items-center justify-between">
          {label && (
            <Label className="text-sm font-medium">
              {label}
            </Label>
          )}
          {showValue && (
            <span 
              className={cn(
                "text-sm font-semibold",
                color === 'risk' ? getRiskColor(value) : 'text-foreground'
              )}
            >
              {value}
            </span>
          )}
        </div>
      )}
      
      <div className="relative px-1">
        <Slider
          value={[value]}
          onValueChange={handleValueChange}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          className={cn(
            "w-full",
            color === 'risk' && value >= 60 && "[&_[role=slider]]:border-risk-high",
            color === 'risk' && value >= 80 && "[&_[role=slider]]:border-risk-critical"
          )}
          aria-label={label || 'Weight slider'}
        />
        
        {showMarks && (
          <div className="flex justify-between mt-2 px-1">
            {marks.map((mark) => (
              <div
                key={mark.value}
                className="flex flex-col items-center"
              >
                <div 
                  className="w-0.5 h-2 bg-muted-foreground/30"
                  style={{
                    marginLeft: `${(mark.value / max) * 100}%`
                  }}
                />
                <span className="text-xs text-muted-foreground mt-1">
                  {mark.label}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {color === 'risk' && (
        <div className="text-xs text-muted-foreground">
          {value < 30 && "Low risk weight"}
          {value >= 30 && value < 60 && "Medium risk weight"}
          {value >= 60 && value < 80 && "High risk weight"}
          {value >= 80 && "Critical risk weight"}
        </div>
      )}
    </div>
  )
}